import React, { useState, useEffect } from 'react';
import { Modal, ModalContent, ModalHeader, ModalBody, Chip } from '@heroui/react';
import { ResponsiveModal, Typography } from '../../../components';
import { CalendarIcon, EmptyContentIc } from '../../../components/icons';
import { UniversalTable } from '../../../components/AccountTable';
import partnerAuthService from '../../../api/partner/partnerAuthService';
import { useTranslations } from '../../../contexts/translation.context';
import { formatDate } from '../../../utils/formatTimeAgo';

interface WithdrawalOrder {
  created_at: string;
  amount: string;
  description: string;
  status: 'paid' | 'pending' | 'cancelled' | 'cancel' | 'processing';
  address: string;
  id: string;
}

interface WithdrawalHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const WithdrawalHistoryModal: React.FC<WithdrawalHistoryModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslations();
  const [withdrawals, setWithdrawals] = useState<WithdrawalOrder[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Состояния для пагинации
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState<
    | {
        page: number;
        page_size: number;
        count: number;
      }
    | undefined
  >(undefined);

  const fetchWithdrawals = async (page: number = 1, size: number = 10) => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await partnerAuthService.getWithdrawalOrders({
        page: page,
        page_size: size,
      });

      if (response.data) {
        // Если API возвращает массив напрямую
        if (Array.isArray(response.data)) {
          setWithdrawals(response.data);
          setPagination({
            page: page,
            page_size: size,
            count: response.data.length,
          });
        } else {
          // Если API возвращает объект с пагинацией
          setWithdrawals(response.data.objects || response.data);
          setPagination({
            page: response.data.page || page,
            page_size: response.data.page_size || size,
            count: response.data.count || response.data.length,
          });
        }
      }
    } catch (error) {
      console.error('Failed to fetch withdrawal history:', error);
      setError(t('wallet.withdrawalHistory.errors.loadFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchWithdrawals(currentPage, pageSize);
    }
  }, [isOpen, currentPage, pageSize]);

  const getStatusChip = (status: string) => {
    switch (status) {
      case 'paid':
        return {
          text: t('wallet.withdrawalHistory.status.paid'),
          color: '#17C964' as const,
          variant: 'flat' as const,
        };
      case 'pending':
        return {
          text: t('wallet.withdrawalHistory.status.pending'),
          color: '#F5A524' as const,
          variant: 'flat' as const,
        };
      // case 'processing':
      //   return {
      //     text: 'Обработка',
      //     color: '#0070F0' as const,
      //     variant: 'flat' as const
      //   };
      case 'cancel':
      case 'cancelled':
        return {
          text: t('wallet.withdrawalHistory.status.cancelled'),
          color: '#F31260' as const,
          variant: 'flat' as const,
        };
      default:
        return {
          text: status,
          color: '#71717A' as const,
          variant: 'flat' as const,
        };
    }
  };

  const columns = [
    // { name: t('wallet.withdrawalHistory.columns.id'), key: "id" },
    { name: t('wallet.withdrawalHistory.columns.amount'), key: 'amount' },
    // { name: t('wallet.withdrawalHistory.columns.address'), key: "address" },
    { name: t('wallet.withdrawalHistory.columns.status'), key: 'status' },
    { name: t('wallet.withdrawalHistory.columns.date'), key: 'date' },
  ];

  const renderCell = (item: WithdrawalOrder, columnKey: string) => {
    const statusChip = getStatusChip(item.status);

    switch (columnKey) {
      case 'id':
        return (
          <Typography variant="body2" className="text-auth-ink text-sm">
            {item.id}
          </Typography>
        );
      case 'amount':
        return (
          <span className="text-auth-ink text-base font-semibold">
            ${parseFloat(item.amount).toFixed(2)}
          </span>
        );
      case 'address':
        return (
          <Typography variant="body2" className="text-auth-muted text-sm truncate max-w-[12.5rem]">
            {item.address || '-'}
          </Typography>
        );
      case 'status':
        return (
          <div className="flex w-full items-start justify-start">
            <div className="bg-[#F4F4F5] flex items-center gap-1 h-6 px-2 rounded-2">
              <div
                className="size-[0.4375rem] rounded-full"
                style={{ backgroundColor: statusChip.color }}
              />
              <Typography variant="body3" className="text-auth-ink">
                {statusChip.text}
              </Typography>
            </div>
          </div>
        );
      case 'date':
        return (
          <div className="flex items-center gap-2">
            <CalendarIcon />
            <span className="text-auth-muted text-xs">{formatDate(item.created_at)}</span>
          </div>
        );
      default:
        return null;
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchWithdrawals(page, pageSize);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
    fetchWithdrawals(1, size);
  };

  const customEmptyContent = (
    <div className="text-center py-12 w-full">
      <div className="flex flex-col items-center gap-4">
        <EmptyContentIc />
        <div className="flex flex-col max-w-[27.5rem] gap-1.5">
          <Typography variant="h6" className="text-xl font-semibold leading-7">
            {t('wallet.withdrawalHistory.emptyTitle')}
          </Typography>
          <Typography className="text-neutral-500 text-base font-medium leading-6" variant="body1">
            {t('wallet.withdrawalHistory.emptyDescription')}
          </Typography>
        </div>
      </div>
    </div>
  );

  return (
    <ResponsiveModal
      isOpen={isOpen}
      onClose={onClose}
      size="5xl"
      scrollBehavior="inside"
      classNames={{
        base: 'max-h-[90vh]',
        wrapper: 'z-[9999]',
        backdrop: 'z-[9998]',
      }}
    >
      <ModalContent className="md:w-[62.5rem] md:max-w-[62.5rem]">
        <ModalHeader className="flex flex-col gap-1 px-4 md:px-6 py-4 ">
          <Typography variant="h6" className="font-semibold text-2xl leading-7">
            {t('wallet.withdrawalHistory.title')}
          </Typography>
        </ModalHeader>

        <ModalBody className="px-4 md:px-6 py-5">
          <UniversalTable
            mobileSize="sm"
            data={withdrawals}
            columns={columns}
            renderCell={renderCell}
            emptyContent={customEmptyContent}
            pagination={pagination}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            currentPage={currentPage}
            currentPageSize={pageSize}
          />
        </ModalBody>
      </ModalContent>
    </ResponsiveModal>
  );
};

export default WithdrawalHistoryModal;
